import { Component } from '@theme/component';

/* Window (ms) after compositionend in which a keydown is still treated as
   the IME confirm/cancel press. Covers Safari, where compositionend fires
   BEFORE the keydown that ended the composition (WebKit Bug 165004). */
const COMPOSITION_CONFIRM_WINDOW_MS = 150;

/* Debounce between the last keystroke and a Predictive Search fetch. */
const INPUT_DEBOUNCE_MS = 150;

/* Resource types requested from the Predictive Search API, matching the
   `types` documented on the `predictive_search` Liquid object. */
const RESOURCE_TYPES = ['product', 'collection', 'article', 'page'].join(',');

/* Per-type cap for suggestion results (products, collections, articles,
   pages). `resources[limit_scope]=each` keeps the total response small. */
const RESOURCE_LIMIT = '4';

/**
 * Header mega search panel — native Predictive Search while typing.
 *
 * Typing (150 ms debounce) fetches the Predictive Search API
 * (/search/suggest?section_id=predictive-search) and transplants the
 * server-rendered suggestions — product cards, collection chips, article
 * and page rows, or the empty state — into the panel. The response is ONE
 * section (sections/predictive-search.liquid), not the full /search page,
 * so every keystroke costs a small server-rendered fragment instead of a
 * complete search page.
 *
 * The panel answers the first keystroke immediately: the results region
 * opens in its animated searching state (spinner + busy pulse) before the
 * debounce window or the network round trip, so the search never looks
 * unresponsive.
 *
 * Enter submits the form and navigates to the full results page unless the
 * query is empty or an IME composition is in progress (on Safari the
 * confirm-Enter keydown arrives just after compositionend, so "in
 * progress" also covers a short window following it). IME composition is
 * otherwise unaffected: candidate text searches live as-is.
 *
 * The panel's close resets input, results and in-flight requests (the
 * response cache survives reopen).
 */
export class HeaderSearchComponent extends Component {
  requiredRefs = ['input', 'form', 'results', 'loading', 'list'];
  #debounce = null;
  #controller = null;
  #cache = new Map();
  #lastQuery = '';
  #panelObserver = null;
  #panel = null;
  #composing = false;
  #lastCompositionEnd = -Infinity;
  #predictiveUrl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#panel = this.closest('#mega-panel');
    this.#predictiveUrl = new URL(
      this.dataset.predictiveUrl || `${window.Shopify.routes.root}search/suggest`,
      window.location.href
    );

    this.refs.input?.addEventListener('input', this.#onInput);
    this.refs.input?.addEventListener('focus', this.#onFocus);
    this.refs.input?.addEventListener('compositionstart', this.#onCompositionStart);
    this.refs.input?.addEventListener('compositionend', this.#onCompositionEnd);
    this.refs.form?.addEventListener('submit', this.#onSubmit);
    this.refs.input?.setAttribute('aria-expanded', 'false');

    if (this.#panel) {
      this.#panelObserver = new MutationObserver(() => {
        if (!this.#panel.classList.contains('is-open')) this.#reset();
      });
      this.#panelObserver.observe(this.#panel, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.input?.removeEventListener('input', this.#onInput);
    this.refs.input?.removeEventListener('focus', this.#onFocus);
    this.refs.input?.removeEventListener('compositionstart', this.#onCompositionStart);
    this.refs.input?.removeEventListener('compositionend', this.#onCompositionEnd);
    this.refs.form?.removeEventListener('submit', this.#onSubmit);
    this.#panelObserver?.disconnect();
    this.#panelObserver = null;
    this.#clearDebounce();
    this.#abort();
  }

  /* ---- input flow ---- */

  #onInput = () => {
    this.#clearDebounce();
    const q = this.refs.input?.value?.trim();
    if (!q) {
      this.#hideResults();
      this.#lastQuery = '';
      this.#setLoading(false);
      this.#showDefaults();
      return;
    }
    this.#hideDefaults();
    this.#updateOpenAll(q);
    /* Instant feedback: reveal the panel in its animated searching state
       on the very first keystroke, before the debounce window or the
       network round trip. Same-query re-runs (cache hits) skip the flash. */
    this.#setLoading(q !== this.#lastQuery || this.refs.results?.hidden);
    this.#debounce = setTimeout(() => this.#load(q), INPUT_DEBOUNCE_MS);
  };

  #onFocus = () => {
    const q = this.refs.input?.value?.trim();
    if (q) {
      this.#updateOpenAll(q);
      this.#load(q);
    }
  };

  #onCompositionStart = () => {
    this.#composing = true;
  };

  #onCompositionEnd = () => {
    this.#composing = false;
    /* Safari fires compositionend BEFORE the keydown that confirmed the
       composition (WebKit Bug 165004), so when that keydown arrives this
       flag is already false. Timestamp the end instead: a keydown landing
       within COMPOSITION_CONFIRM_WINDOW_MS is IME confirmation. */
    this.#lastCompositionEnd = performance.now();
  };

  #onSubmit = (event) => {
    /* Enter navigates to the full results page via the form's native GET
       submission. Intercept only an empty query, or an IME-confirmation
       Enter: one while a composition is still active (#composing), or —
       Safari-only, WebKit Bug 165004 — one whose keydown arrives just
       AFTER compositionend, caught by the timestamp window. Confirming
       candidates with Enter must never navigate away. */
    const justEndedComposition =
      performance.now() - this.#lastCompositionEnd < COMPOSITION_CONFIRM_WINDOW_MS;
    if (this.refs.input?.value?.trim() && !this.#composing && !justEndedComposition) {
      return;
    }
    event.preventDefault();
  };

  /* ---- fetching ---- */

  #queryUrl(q) {
    const url = new URL(this.#predictiveUrl);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', RESOURCE_TYPES);
    url.searchParams.set('resources[limit]', RESOURCE_LIMIT);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  #load(q) {
    if (q === this.#lastQuery && this.refs.results && !this.refs.results.hidden) return;
    this.#lastQuery = q;

    const key = q;
    const cached = this.#cache.get(key);
    if (cached !== undefined) {
      /* cache hit — the most recent query (backspace through history)
         comes back instantly: drop the loading state, then render */
      this.#cache.delete(key);
      this.#cache.set(key, cached);
      this.#setLoading(false);
      this.#render(cached);
      return;
    }

    this.#abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.#setLoading(true);

    fetch(this.#queryUrl(q), {
      headers: { Accept: 'text/html' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (controller.signal.aborted) return;
        this.#cacheSet(key, text);
        this.#render(text);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.#hideResults();
      })
      .finally(() => {
        if (this.#controller === controller) {
          this.#controller = null;
          this.#setLoading(false);
        }
      });
  }

  #setLoading(loading) {
    if (loading) {
      this.setAttribute('aria-busy', 'true');
      /* The loading placeholder lives inside the results region — reveal
         the region too, or the spinner would stay invisible beneath the
         `hidden` attribute until the first response arrives. */
      if (this.refs.results) this.refs.results.hidden = false;
      if (this.refs.loading) this.refs.loading.hidden = false;
      this.refs.input?.setAttribute('aria-expanded', 'true');
    } else {
      this.removeAttribute('aria-busy');
      if (this.refs.loading) this.refs.loading.hidden = true;
    }
  }

  #abort() {
    if (this.#controller) {
      this.#controller.abort();
      this.#controller = null;
    }
  }

  #cacheSet(key, html) {
    if (this.#cache.has(key)) this.#cache.delete(key);
    this.#cache.set(key, html);
    if (this.#cache.size > 12) {
      this.#cache.delete(this.#cache.keys().next().value);
    }
  }

  /* ---- rendering ---- */

  #render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const section = doc.querySelector('.predictive-search');
    if (!section) {
      throw new Error('Predictive search response is missing .predictive-search root');
    }

    if (this.refs.list) {
      this.refs.list.replaceChildren(section.cloneNode(true));
    }
    this.#showResults();
  }

  #updateOpenAll(q) {
    if (!this.refs.openAll) return;
    const url = new URL(
      this.refs.form?.action || `${window.Shopify.routes.root}search`,
      window.location.href
    );
    url.searchParams.set('q', q);
    this.refs.openAll.href = url.pathname + url.search;
  }

  #showResults() {
    if (this.refs.results) this.refs.results.hidden = false;
    this.refs.input?.setAttribute('aria-expanded', 'true');
  }

  #hideResults() {
    if (this.refs.results) this.refs.results.hidden = true;
    this.refs.input?.setAttribute('aria-expanded', 'false');
  }

  /* ---- default (empty-query) state ---- */

  /* The panel opens on a curated "featured articles" state; typing swaps
     it for the live searching/results states, and clearing the input
     brings it back. */

  #showDefaults() {
    if (this.refs.defaults) this.refs.defaults.hidden = false;
  }

  #hideDefaults() {
    if (this.refs.defaults) this.refs.defaults.hidden = true;
  }

  /* ---- panel close: back to a clean slate ---- */

  #reset() {
    if (this.refs.input) this.refs.input.value = '';
    this.#hideResults();
    this.#clearDebounce();
    this.#abort();
    this.#lastQuery = '';
    this.#setLoading(false);
    this.#showDefaults();
  }

  #clearDebounce() {
    if (this.#debounce) {
      clearTimeout(this.#debounce);
      this.#debounce = null;
    }
  }
}

if (!customElements.get('header-search-component')) {
  customElements.define('header-search-component', HeaderSearchComponent);
}